import _ from 'lodash'
import KeyValuePair from '@/modules/core/support/KeyValuePair';
import MenuType from '@/modules/cms/modules/menu/model/MenuType'
import MenuEntryTranslation from './MenuEntryTranslation';
import { ResourcePointer } from '@/modules/cms/types';
import MenuStyle, {StyleSet} from '@/modules/cms/modules/menu/model/MenuStyle';

export interface FindPathResult {
    item: MenuEntry | null
    parent: MenuEntry | null
}

/**
 * Entrada de menu
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
export default class MenuEntry implements ResourcePointer {
    public readonly tempId = _.uniqueId('menu_')
    public id = 0
    public type = MenuType.None
    public name = ''
    public slot = ''
    public itemCount = 0
    public items: MenuEntry[] = []
    public translations: MenuEntryTranslation[] = []
    public createdAt = 0
    public updatedAt = 0
    public link = ''
    public attributes: KeyValuePair = {}

    /**
     * Configuración de estilos para el elemento del menú
     */
    public styles = new StyleSet()

    // Campos de la interfaz para el puntero de un recurso
    public resourceId = ''
    public resourceType = ''

    /**
     * Indica si el menú es nuevo o existe en el servidor
     */
    public get isNew (): boolean {
        return this.id === 0
    }

    /**
     * Obtiene una traducción del menu o devuelve una nueva
     *
     * @param locale { string }
     */
    public translate (locale: string): MenuEntryTranslation {
        let translation = this.translations.find(t => t.locale === locale)
        if (translation) {
            return translation
        }

        // La traducción no existe se crea un nueva y se inserta
        translation = new MenuEntryTranslation(locale)
        this.translations.push(translation)

        return translation
    }

    /**
     * Alias de MenuEntry.items
     */
    get children (): MenuEntry[] {
        return this.items
    }

    set children (children: MenuEntry[]) {
        this.items = children
    }

    /**
     * Indica si el menu es una colección de elementos
     */
    get isCollection (): boolean {
        return this.type === MenuType.Collection
    }

    /**
     * Indica si el menu es maestro, un menu principal sin padre
     */
    get isMain (): boolean {
        return this.type === MenuType.Main
    }

    get isLink (): boolean {
        return this.type === MenuType.Link
    }

    get isResource (): boolean {
        return this.type === MenuType.Resource
    }

    /**
     * Crea una entidad MenuEntry en base a la información plana recibida desde
     * el servicio
     *
     * @param data
     */
    public static deserialize (data: KeyValuePair) {
        const menu = new this()
        menu.id = data.id
        menu.type = data.type
        menu.name = data.name
        menu.slot = data.slot
        menu.itemCount = data.item_count || 0
        menu.createdAt = data.created_at || 0
        menu.updatedAt = data.updated_at || 0
        menu.link = data.link || ''
        menu.resourceId = data.resource_id
        menu.resourceType = data.resource_type
        menu.attributes = data.attributes || {}

        // Si los atributos del menú incluyen la configuración de estilos estos
        // son convertidos en una entidad y almacenados en la propiedad
        // MenuEntry.styles para facilitar su uso
        if (menu.attributes.styles) {
            const styles = menu.attributes.styles
            Object.keys(styles).forEach(key => {
                if (!(key in styles)) {
                    return
                }

                menu.styles[key] = Object.assign(new MenuStyle(), styles[key])
            })
        }

        // Se cargan los hijos del menu si es de tipo colección o es un menú
        // maestro
        const items = data.items || []
        menu.items = items.map((i: KeyValuePair) => MenuEntry.deserialize(i))
        menu.translations = data.translations.map((t: KeyValuePair) => MenuEntryTranslation.deserialize(t))

        return menu
    }

    public serialize (): KeyValuePair {
        return this.doSerialize(this)
    }

    private doSerialize (entry: MenuEntry) {
        const data: KeyValuePair = {
            type: entry.type,
            name: entry.name,
            items: [],
            slot: entry.slot,
            link: entry.link,
            resource_type: entry.resourceType,
            resource_id: entry.resourceId,
            attributes: entry.attributes,
        }

        if (!_.isEmpty(entry.styles)) {
            Object.keys(entry.styles).forEach(size => {
                if (!(size in entry.styles)) {
                    return
                }

                _.set(data.attributes, `styles.${size}`, Object.assign({}, entry.styles[size]))
            })
        }

        if (entry.items.length) {
            data.items = entry.items.map(i => this.doSerialize(i))
        }

        data.translations = entry.translations.map(t => this.serializeTranslation(t))

        return data
    }

    /**
     * Serializa una traducción
     *
     * @param translation { KeyValuePair }
     */
    private serializeTranslation (translation: MenuEntryTranslation): KeyValuePair {
        return {
            locale: translation.locale,
            title: translation.title,
        }
    }

    /**
     * Indica si el elemento es un menu anidable, que puede tener hijos
     */
    public get isAnidable (): boolean {
        return this.type === MenuType.Collection || this.type === MenuType.Main
    }


    /**
     * Busca un elemento por su id en la aplicación
     *
     * @param id {string}
     */
    public findItem (id: string, recursive = false): MenuEntry | null {
        return this.doFindItem(this.items, 'id', id)
    }

    /**
     * Busca un elemento por su id temporal en tiempo de ejecución
     *
     * @param tempId
     * @param recursive
     */
    public findItemByTempId (tempId: string, recursive = false): MenuEntry | null {
        if (this.tempId === tempId) {
            return this
        }

        return this.doFindItem(this.items, 'tempId', tempId, recursive)
    }


    /**
     * Busca un elemento en la lista de elementos hijos
     *
     * @param items
     * @param field
     * @param value
     * @param recursive
     */
    private doFindItem (items: MenuEntry[], field: string, value: any, recursive = false): MenuEntry | null {
        for (const item of items) {
            const id = _.get(item, field)
            if (!id) {
                continue
            }

            if (id === value) {
                return item
            }

            if (item.items.length && recursive) {
                const result =  this.doFindItem(item.items, field, value, recursive)
                if (result) {
                    return result
                }
            }
        }

        return null
    }

    /**
     * Actualiza un elemento
     *
     * @param id
     * @param item
     */
    public updateItem (id: string, item: MenuEntry): void {
        this.items = this.doUpdateItem(this.items, id, item)
    }

    /**
     * Actualiza un elemento del menu, de forma recursiva
     *
     * @param items
     * @param id
     * @param item
     */
    private doUpdateItem (items: MenuEntry[], id: string, newItem: MenuEntry) {
        for (const index in items) {
           if (!items[index]) {
               continue
           }

           if (items[index].tempId === id) {
               items[index] = newItem

               return items
           }

           items[index].items = this.doUpdateItem(items, id, newItem)
        }

        return items
    }

    /**
     * Obtiene la ruta de acceso de un nodo
     * @param id { string }
     */
    public getPath (id: string): string {
        return this.doGetPath(this.items, id, `/${this.tempId}`)
    }

    /**
     *
     * @param items
     * @param id
     * @param path
     */
    private doGetPath (items: MenuEntry[], id: string, path: string): string {
        for (const item of items) {
            if (item.tempId === id) {
                return `${path}/${item.tempId}`
            }

            if (item.items.length) {
                return this.doGetPath(item.items, id, `${path}/${item.tempId}`)
            }
        }

        return ''
    }

    /**
     * Elimina un elemento de forma recursiva en base a su id temporal
     */
    public deleteItem (id: string) {
        this.items = this.doDeleteItem(this.items, id)
    }

    /**
     * Actualiza un elemento del menu, de forma recursiva
     *
     * @param items
     * @param id
     * @param item
     */
    private doDeleteItem (items: MenuEntry[], id: string) {
        for (const index in items) {
           if (!items[index]) {
               continue
           }

           if (items[index].tempId === id) {
               items.splice(Number.parseInt(index, 10), 1)

               return items
           }

           items[index].items = this.doDeleteItem(items[index].items, id)
        }

        return items
    }
}
